/**
 * Control Plane editions and folding (CORD-04): versioned, chained, per-entity
 * state validated against an owner-rooted roster. Every member folds the same
 * chain and lands on the same head.
 */

import type { DecodedStreamEvent } from "./types";
import { rumorTimeMs } from "./stream";
import { concatBytes, hexToBytes, bytesToHex, sha256, utf8 } from "./crypto";

export const PERM = {
  MANAGE_ROLES: 1n << 0n,
  MANAGE_CHANNELS: 1n << 1n,
  MANAGE_METADATA: 1n << 2n,
  KICK: 1n << 3n,
  BAN: 1n << 4n,
  MANAGE_MESSAGES: 1n << 5n,
  CREATE_INVITE: 1n << 6n,
  VIEW_AUDIT_LOG: 1n << 8n,
  MENTION_EVERYONE: 1n << 9n,
  PIN_MESSAGES: 1n << 11n,
} as const;

const ALL_PERMS = Object.values(PERM).reduce((a, b) => a | b, 0n);

export const VSK = {
  METADATA: 0,
  ROLE: 1,
  CHANNEL: 2,
  GRANT: 3,
  BANLIST: 4,
  INVITE_REGISTRY: 8,
  DISSOLVED: 10,
  PINS: 11,
} as const;

export interface Edition {
  vsk: number;
  eid: string;
  ev: number;
  ep?: string;
  actor: string;
  content: string;
  createdAtMs: number;
  rumorId: string;
}

export function parseEdition(d: DecodedStreamEvent): Edition | null {
  const { rumor } = d;
  if (rumor.kind !== 3308) return null;
  const vsk = Number(rumor.tags.find(([n]) => n === "vsk")?.[1]);
  const eid = rumor.tags.find(([n]) => n === "eid")?.[1];
  const ev = Number(rumor.tags.find(([n]) => n === "ev")?.[1]);
  const ep = rumor.tags.find(([n]) => n === "ep")?.[1];
  if (!Number.isInteger(vsk) || !eid || !/^[0-9a-f]{64}$/.test(eid)) return null;
  if (!Number.isInteger(ev) || ev < 1) return null;
  const createdAtMs = rumorTimeMs(rumor);
  if (createdAtMs === null) return null;
  return {
    vsk,
    eid,
    ev,
    ep,
    actor: rumor.pubkey,
    content: rumor.content,
    createdAtMs,
    rumorId: rumor.id,
  };
}

function u64be(n: number | bigint): Uint8Array {
  const out = new Uint8Array(8);
  new DataView(out.buffer).setBigUint64(0, BigInt(n), false);
  return out;
}

/**
 * The edition hash — a length-prefixed, domain-separated digest (CORD-04 §1)
 * cited by the next edition's `ep` and by `vac` authority citations.
 */
export async function editionHash(e: {
  eid: string;
  ev: number;
  ep?: string;
  content: string;
}): Promise<string> {
  const label = utf8("vector-community/v1/edition");
  const content = utf8(e.content);
  const prev = e.ep ? hexToBytes(e.ep) : new Uint8Array(32);
  const digest = await sha256(
    concatBytes(
      u64be(label.length),
      label,
      hexToBytes(e.eid),
      u64be(e.ev),
      ...(e.ep ? [new Uint8Array([1]), prev] : [new Uint8Array([0]), prev]),
      u64be(content.length),
      content,
    ),
  );
  return bytesToHex(digest);
}

export interface RoleState {
  id: string;
  name: string;
  position: number;
  permissions: bigint;
  color: number;
}

export interface ChannelState {
  id: string;
  name: string;
  private: boolean;
  deleted: boolean;
}

export interface CommunityMetadata {
  name: string;
  description?: string;
  relays: string[];
  custom?: Record<string, unknown>;
}

export interface ControlState {
  metadata: CommunityMetadata | null;
  channels: ChannelState[];
  roles: Map<string, RoleState>;
  /** member pubkey → granted role ids. */
  grants: Map<string, string[]>;
  banlist: Set<string>;
  /** Accepted head edition per entity, keyed `${vsk}:${eid}`. */
  heads: Map<string, Edition>;
}

function parseRole(e: Edition): RoleState | null {
  try {
    const c = JSON.parse(e.content) as Record<string, unknown>;
    const position = Number(c.position);
    if (!Number.isInteger(position) || position < 1) return null; // position 0 is reserved
    const permissions =
      typeof c.permissions === "string"
        ? BigInt(c.permissions)
        : BigInt(Number(c.permissions ?? 0));
    const name = String(c.name ?? "");
    if (!name) return null;
    return {
      id: e.eid,
      name,
      position,
      permissions,
      color: Number(c.color ?? 0),
    };
  } catch {
    return null;
  }
}

export interface Rank {
  position: number;
  permissions: bigint;
}

/** Resolve a member's rank in the owner-rooted roster (CORD-04 §3). */
export function memberRank(
  pubkey: string,
  owner: string,
  roles: Map<string, RoleState>,
  grants: Map<string, string[]>,
): Rank {
  if (pubkey === owner) return { position: 0, permissions: ALL_PERMS };
  let position = Infinity;
  let permissions = 0n;
  for (const rid of grants.get(pubkey) ?? []) {
    const role = roles.get(rid);
    if (!role) continue;
    position = Math.min(position, role.position);
    permissions |= role.permissions;
  }
  return { position, permissions };
}

/** Does `actor` hold `bit` and (for ranked targets) strictly outrank? */
function holds(rank: Rank, bit: bigint): boolean {
  return (rank.permissions & bit) !== 0n;
}

/**
 * Fold the Control Plane: pick the highest-version authority-valid head per
 * entity. Authority is resolved iteratively — the owner roots the roster,
 * accepted Roles and Grants extend it, and newly authorized staff editions
 * extend it further (CORD-04 §1, refuse-downgrade fold).
 */
export function foldControlPlane(
  events: DecodedStreamEvent[],
  owner: string,
): ControlState {
  const editions = events
    .map(parseEdition)
    .filter((e): e is Edition => e !== null);

  // Highest-ev candidate per entity.
  const candidates = new Map<string, Edition>();
  for (const e of editions) {
    const key = `${e.vsk}:${e.eid}`;
    const cur = candidates.get(key);
    if (!cur || e.ev > cur.ev || (e.ev === cur.ev && e.rumorId < cur.rumorId)) {
      candidates.set(key, e);
    }
  }

  const heads = new Map<string, Edition>();
  const rebuildRoster = () => {
    const roles = new Map<string, RoleState>();
    const grants = new Map<string, string[]>();
    for (const e of heads.values()) {
      if (e.vsk === VSK.ROLE) {
        const role = parseRole(e);
        if (role) roles.set(role.id, role);
      } else if (e.vsk === VSK.GRANT) {
        try {
          const c = JSON.parse(e.content) as {
            member?: string;
            role_ids?: string[];
          };
          if (c.member && Array.isArray(c.role_ids)) {
            grants.set(c.member, c.role_ids.filter((r) => typeof r === "string"));
          }
        } catch {
          /* malformed grant */
        }
      }
    }
    return { roles, grants };
  };

  const isAuthorized = (e: Edition, roles: Map<string, RoleState>, grants: Map<string, string[]>): boolean => {
    const rank = memberRank(e.actor, owner, roles, grants);
    switch (e.vsk) {
      case VSK.METADATA:
        return holds(rank, PERM.MANAGE_METADATA);
      case VSK.CHANNEL:
        return holds(rank, PERM.MANAGE_CHANNELS);
      case VSK.ROLE: {
        if (!holds(rank, PERM.MANAGE_ROLES)) return false;
        const role = parseRole(e);
        // No edition may claim a position at or above its own signer.
        return role !== null && rank.position < role.position;
      }
      case VSK.GRANT: {
        if (!holds(rank, PERM.MANAGE_ROLES)) return false;
        try {
          const c = JSON.parse(e.content) as { role_ids?: string[] };
          // The signer must strictly outrank every role handed out.
          return (c.role_ids ?? []).every((rid) => {
            const role = roles.get(rid);
            return role !== undefined && rank.position < role.position;
          });
        } catch {
          return false;
        }
      }
      case VSK.BANLIST:
        return holds(rank, PERM.BAN);
      default:
        // Unknown entities fold without authority (e.g. invite registries,
        // pins): accepted but never interpreted by this client.
        return e.actor === owner;
    }
  };

  // Iteratively accept heads as the roster grows (owner roots the fixpoint).
  for (let pass = 0; pass < 6; pass++) {
    const { roles, grants } = rebuildRoster();
    let changed = false;
    for (const [key, e] of candidates) {
      if (heads.has(key)) continue;
      if (e.actor === owner || isAuthorized(e, roles, grants)) {
        heads.set(key, e);
        changed = true;
      }
    }
    if (!changed) break;
  }

  const { roles, grants } = rebuildRoster();

  // Resolve the banlist, then drop banned actors' editions everywhere.
  const banlist = new Set<string>();
  for (const e of heads.values()) {
    if (e.vsk !== VSK.BANLIST) continue;
    try {
      const list = JSON.parse(e.content) as string[];
      for (const pk of list) {
        if (typeof pk === "string" && /^[0-9a-f]{64}$/.test(pk)) banlist.add(pk);
      }
    } catch {
      /* malformed banlist */
    }
  }
  banlist.delete(owner);
  for (const [key, e] of heads) {
    if (banlist.has(e.actor)) heads.delete(key);
  }

  // Materialize state.
  let metadata: CommunityMetadata | null = null;
  const channels: ChannelState[] = [];
  for (const e of heads.values()) {
    if (e.vsk === VSK.METADATA) {
      try {
        const c = JSON.parse(e.content) as Record<string, unknown>;
        metadata = {
          name: String(c.name ?? ""),
          description:
            typeof c.description === "string" ? c.description : undefined,
          relays: Array.isArray(c.relays)
            ? (c.relays.filter((r) => typeof r === "string") as string[]).slice(0, 8)
            : [],
          custom:
            c.custom && typeof c.custom === "object"
              ? (c.custom as Record<string, unknown>)
              : undefined,
        };
      } catch {
        /* malformed metadata */
      }
    } else if (e.vsk === VSK.CHANNEL) {
      try {
        const c = JSON.parse(e.content) as Record<string, unknown>;
        channels.push({
          id: e.eid,
          name: String(c.name ?? ""),
          private: c.private === true,
          deleted: c.deleted === true,
        });
      } catch {
        /* malformed channel */
      }
    }
  }
  channels.sort((a, b) => (a.id < b.id ? -1 : 1));

  return { metadata, channels, roles, grants, banlist, heads };
}
